import { useState, useCallback, useRef, useEffect } from 'react';
import { warehouseService } from '../services/warehouseService';
import { productService } from '../services/productService';
import api from '../services/api';

const ITEMS_PER_PAGE = 20;

/**
 * Hook zarządzający danymi inwentarza magazynowego.
 * Obsługuje ładowanie produktów, filtrowanie, paginację, magazyny, statystyki.
 */
const useWarehouseInventory = (currentLocationId) => {
  // State danych
  const [inventory, setInventory] = useState({
    products: [],
    pagination: { page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 }
  });
  const [currentFilters, setCurrentFilters] = useState({
    search: '', category: '', available_only: true
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});

  // State UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventoryView, setInventoryView] = useState('available');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  // Refs do unikania dependency issues
  const isInitializedRef = useRef(false);
  const filtersRef = useRef(currentFilters);
  const pageRef = useRef(currentPage);
  const locationIdRef = useRef(currentLocationId);
  const isLoadingRef = useRef(false);
  const prevLocationIdRef = useRef(null);
  const prevLocForWarehousesRef = useRef(null);
  const prevSelectedWarehouseRef = useRef('all');

  // Synchronizacja refs
  filtersRef.current = currentFilters;
  pageRef.current = currentPage;
  locationIdRef.current = currentLocationId;

  // Inicjalizacja z cleanup
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const initData = async () => {
      if (isInitializedRef.current) return;
      isInitializedRef.current = true;
      setLoading(true);
      setError('');

      try {
        // Laduj rownolegle: categories, inventory, stats
        const [categoriesResult, inventoryResult, statsResult] = await Promise.all([
          productService.getCategories(controller.signal),
          warehouseService.getInventory({
            page: 1, limit: ITEMS_PER_PAGE, search: '', category: '',
            available_only: true, location_id: currentLocationId,
            signal: controller.signal
          }),
          warehouseService.getStats(currentLocationId),
        ]);

        if (!isMounted) return;

        if (categoriesResult.success) {
          setCategories(categoriesResult.data || []);
        }
        if (inventoryResult.success) {
          setInventory({
            products: inventoryResult.data?.products || [],
            pagination: inventoryResult.data?.pagination || { page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 }
          });
        } else {
          setError('Nie udało się załadować produktów');
        }
        if (statsResult.success) {
          setStats(statsResult.data || {});
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        console.error('Błąd ładowania danych magazynu:', err);
        if (isMounted) setError('Nie udało się załadować danych magazynu');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [currentLocationId]);

  // Ładuj po zmianie lokalizacji z cleanup
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const locationChanged = prevLocationIdRef.current !== null &&
                            prevLocationIdRef.current !== currentLocationId;
    prevLocationIdRef.current = currentLocationId;

    if (isInitializedRef.current && currentLocationId && locationChanged) {
      const timeoutId = setTimeout(async () => {
        if (isLoadingRef.current || !isMounted) return;
        const locationToUse = locationIdRef.current;
        isLoadingRef.current = true;
        setLoading(true);
        setError('');

        try {
          const inventoryParams = {
            page: 1, limit: ITEMS_PER_PAGE,
            search: filtersRef.current.search, category: filtersRef.current.category,
            available_only: filtersRef.current.available_only !== false,
            signal: controller.signal
          };
          if (locationToUse) inventoryParams.location_id = locationToUse;

          const result = await warehouseService.getInventory(inventoryParams);
          if (!isMounted) return;
          if (result.success) {
            setInventory({
              products: result.data?.products || [],
              pagination: result.data?.pagination || { page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 }
            });
            setCurrentPage(1);
          } else {
            setError('Nie udało się załadować produktów');
          }

          const statsResult = await warehouseService.getStats(locationToUse, controller.signal);
          if (!isMounted) return;
          if (statsResult.success) setStats(statsResult.data || {});
        } catch (err) {
          if (err.name === 'AbortError' || err.name === 'CanceledError') return;
          console.error('Błąd ładowania danych magazynu:', err);
          if (isMounted) setError('Nie udało się załadować danych magazynu');
        } finally {
          if (isMounted) setLoading(false);
          isLoadingRef.current = false;
        }
      }, 150);

      return () => {
        clearTimeout(timeoutId);
        isMounted = false;
        controller.abort();
      };
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [currentLocationId]);

  // Ładuj magazyny po zmianie lokalizacji z cleanup
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadWarehousesWithCleanup = async () => {
      const locId = locationIdRef.current;
      if (!locId || isLoadingRef.current) return;

      try {
        const response = await api.get('/warehouses', { signal: controller.signal });
        if (!isMounted) return;
        const data = response.data;
        if (data.success) {
          const locationWarehouses = data.data.filter(w => w.location_id === locId);
          setAvailableWarehouses(locationWarehouses);
          setSelectedWarehouse(prev => {
            if (prev !== 'all') {
              return locationWarehouses.some(w => w.id.toString() === prev) ? prev : 'all';
            }
            return 'all';
          });
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        console.error('Błąd ładowania magazynów:', err);
      }
    };

    if (currentLocationId && prevLocForWarehousesRef.current !== currentLocationId) {
      prevLocForWarehousesRef.current = currentLocationId;
      loadWarehousesWithCleanup();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [currentLocationId]);

  // Odśwież po zmianie magazynu
  useEffect(() => {
    if (selectedWarehouse !== 'all' &&
        prevSelectedWarehouseRef.current !== selectedWarehouse &&
        currentLocationId) {
      prevSelectedWarehouseRef.current = selectedWarehouse;
      handleFilter(filtersRef.current, 1);
    } else {
      prevSelectedWarehouseRef.current = selectedWarehouse;
    }
  }, [selectedWarehouse]);

  // loadInitialData jako funkcja refresh dla zewnętrznych komponentów
  const loadInitialData = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const categoriesResult = await productService.getCategories();
      if (categoriesResult.success) {
        setCategories(categoriesResult.data || []);
      }

      const result = await warehouseService.getInventory({
        page: 1, limit: ITEMS_PER_PAGE,
        search: filtersRef.current.search || '',
        category: filtersRef.current.category || '',
        available_only: filtersRef.current.available_only !== false,
        location_id: currentLocationId
      });

      if (result.success) {
        setInventory({
          products: result.data?.products || [],
          pagination: result.data?.pagination || { page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 }
        });
      } else {
        setError('Nie udało się załadować produktów');
      }

      const statsResult = await warehouseService.getStats(currentLocationId);
      if (statsResult.success) {
        setStats(statsResult.data || {});
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      console.error('Błąd ładowania danych magazynu:', err);
      setError('Nie udało się załadować danych magazynu');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [currentLocationId]);

  const handleFilter = useCallback(async (filters, page = 1) => {
    const locationToUse = locationIdRef.current;
    setLoading(true);
    setError('');

    try {
      // Merge: InventoryFilters wysyła {search, category},
      // przyciski widoku wysylaja {available_only, stock_zero}
      // Jesli przychodzi available_only lub stock_zero - to przycisk widoku, nadpisz
      // Jesli nie - to InventoryFilters, zachowaj obecny widok
      const hasViewKeys = 'available_only' in filters || 'stock_zero' in filters;
      const merged = hasViewKeys
        ? { ...filtersRef.current, ...filters }
        : { ...filters, available_only: filtersRef.current.available_only, stock_zero: filtersRef.current.stock_zero };
      const isStockZero = !!merged.stock_zero;
      const inventoryParams = {
        page, limit: ITEMS_PER_PAGE,
        search: merged.search || '', category: merged.category || '',
        available_only: isStockZero ? false : merged.available_only !== false,
        ...(isStockZero && { stock_zero: true })
      };

      if (selectedWarehouse === 'all' && locationToUse) {
        inventoryParams.location_id = locationToUse;
      } else if (selectedWarehouse !== 'all') {
        inventoryParams.warehouse_id = parseInt(selectedWarehouse, 10);
      }

      const result = await warehouseService.getInventory(inventoryParams);
      if (result.success) {
        setInventory({
          products: result.data?.products || [],
          pagination: result.data?.pagination || { page, limit: ITEMS_PER_PAGE, total: 0, pages: 0 }
        });
        setCurrentPage(page);
        setCurrentFilters(merged);
        return { success: true };
      } else {
        setError(result.error || 'Błąd podczas filtrowania produktów');
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Błąd filtrowania:', err);
      setError('Błąd podczas ładowania produktów');
      return { success: false, error: 'Błąd podczas ładowania produktów' };
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouse]);

  const loadLowStockProducts = useCallback(async () => {
    const locationToUse = locationIdRef.current;
    setLoading(true);
    setError('');

    try {
      const result = await warehouseService.getLowStockProducts(locationToUse);
      if (result.success) {
        const products = Array.isArray(result.data) ? result.data : [];
        setInventory(prev => ({
          products,
          pagination: { ...prev.pagination, total: products.length }
        }));
        setCurrentFilters(prev => ({ ...prev, available_only: false, stock_zero: false }));
        setInventoryView('low-stock');
      } else {
        setError('Nie udało się załadować produktów z niskim stanem');
      }
    } catch (err) {
      console.error('Błąd ładowania produktów z niskim stanem:', err);
      setError('Błąd podczas ładowania produktów z niskim stanem');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSaveProduct = useCallback(async (productId, formData) => {
    try {
      setLoading(true);
      const updateData = {
        name: formData.name, barcode: formData.barcode,
        product_code: formData.product_code, description: formData.description,
        price: parseFloat(formData.price) || 0,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        cena_sprzedazy_netto: parseFloat(formData.cena_sprzedazy_netto) || 0,
        cena_sprzedazy_brutto: parseFloat(formData.cena_sprzedazy_brutto) || 0,
        cena_zakupu_netto: parseFloat(formData.cena_zakupu_netto) || 0,
        cena_zakupu_brutto: parseFloat(formData.cena_zakupu_brutto) || 0,
        stawka_vat: parseFloat(formData.stawka_vat || formData.tax_rate) || 23,
        category_id: formData.category_id || null,
        unit: formData.unit || 'szt'
      };

      const result = await warehouseService.updateProduct(productId, updateData);
      if (result.success) {
        showSuccess('Produkt został zaktualizowany pomyślnie');
        const refreshResult = await handleFilter(filtersRef.current, pageRef.current);
        if (!refreshResult?.success) {
          await handleFilter(filtersRef.current, 1);
        }
      } else {
        throw new Error(result.error || 'Nie udało się zaktualizować produktu');
      }
    } catch (err) {
      console.error('Błąd podczas zapisywania produktu:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleFilter]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => { setShowSuccessAlert(false); setSuccessMessage(''); }, 5000);
  };

  return {
    // Data
    inventory, categories, stats, availableWarehouses, selectedWarehouse,
    currentFilters, currentPage,
    // UI state
    loading, error, activeTab, inventoryView, successMessage, showSuccessAlert,
    // Setters
    setActiveTab, setInventoryView, setSelectedWarehouse, setError,
    setShowSuccessAlert, setSuccessMessage,
    // Actions
    loadInitialData, handleFilter, loadLowStockProducts, handleSaveProduct,
    // Refs
    filtersRef
  };
};

export default useWarehouseInventory;
